import React , { useState, useEffect }  from "react";
import {cleanCompanyName, APP_NAME} from '../config/Common'
import {useFaqsQuery} from 'data/stock-stat-data'
import StockAnalysis from './stock-stat/StockAnalysis'


const formatNumbersDeep = (value, formatter) => {
    if (typeof value === "number") {
        return formatter(value);
    }

    if (Array.isArray(value)) {
        return value.map((v) => formatNumbersDeep(v, formatter));
    }

    if (value !== null && typeof value === "object") {
        return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [
            key,
            formatNumbersDeep(val, formatter),
        ])
        );
    }

    return value; // string, boolean, null, undefined
};

export default function CompanyFAQ({companyinfo, type}) {
    
    const [compName, setCompName] = useState(cleanCompanyName(companyinfo?.name))

    const {data:faqData} = useFaqsQuery({id:companyinfo?.id,type:(type?'Consolidated':'Standalone')})

    const cData = faqData?.data ? faqData?.data :null;
    const extraRatio = faqData?.data?.extra_data ? JSON.parse(faqData?.data?.extra_data):null;

    /* useEffect(() => {
        setCompanyRatio(faqData)
        if(faqData?.data?.extra_data) {
            console.log('faqData=====', JSON.parse(faqData.data.extra_data))
        }
    }, [faqData]) */

    //console.log('company extra info====', cData, extraRatio)

    const compactIN = (num) =>
    new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);

    const addFaq = ({ when, question, answer }) =>
    when ? [{ question, answer }] : [];



//console.log('faq info====', faq)


    const faqs = [        
        ...addFaq({
            when: companyinfo?.nse_price,
            question: `What is the share price of ${compName} today?`,
            answer: `${compName} is currently trading at a share price of Rs ${compactIN(companyinfo?.nse_price)}.`
            
        }),        
        ...addFaq({
            when: companyinfo?.market_cap,
            question: `What is the market capitalisation of ${compName}?`,
            answer: `The market capitalisation of ${compName} stands at approximately Rs ${compactIN(companyinfo?.market_cap)} Cr, based on its latest share price.`
            
        }),
        ...addFaq({
            when: faqData?.data?.pe && faqData?.data?.pb,
            question: `What are the P/E and P/B ratios of ${compName}?`,
            answer: `${compName} has a P/E ratio of ${faqData?.data?.pe} and a P/B ratio of ${faqData?.data?.pb}, indicating how the stock is valued in relation to its earnings and book value.`
        }),
        ...addFaq({
            when: companyinfo?.year_high && companyinfo?.year_low,
            question: `What are the 52-week high and low prices of ${compName} shares?`,
            answer: `The stock's 52-week high is Rs ${companyinfo?.year_high}, while the 52-week low is Rs ${companyinfo?.year_low}.`
        }),
        ...addFaq({
            when: companyinfo?.dy_val !== undefined,
            question: `Does ${compName} pay dividends?`,
            answer: companyinfo?.dy_val > 0
                ? `${compName} pays dividends to its shareholders, with a dividend yield of ${companyinfo?.dy_val}%.`
                : `${compName} currently does not pay dividends, and its dividend yield is 0%.`
        }),
        ...addFaq({
            when: companyinfo?.face_value && faqData?.data?.book_value,
            question: `What are the face value and book value of ${compName} shares?`,
            answer: `The face value of ${compName} shares is Rs ${companyinfo?.face_value}, and the book value per share is around Rs ${faqData?.data?.book_value}. The face value represents the nominal value, whereas the book value reflects the company's accounting worth.`
        }),
        ...addFaq({
            when: extraRatio && extraRatio.debt > 0,
            question:`What is the debt of ${compName}?`,
            answer: `${compName} has a total debt of Rs ${compactIN(extraRatio?.debt)} Cr., which affects investor sentiment and financial stability.`
        }),
        ...addFaq({
            when: faqData?.data?.roe || faqData?.data?.roce,
            question: `What are the ROE and ROCE of ${compName}?`,
            answer: faqData?.data?.roe && faqData?.data?.roce
            ? `${compName} has a ROE of ${faqData?.data?.roe}% and a ROCE of ${faqData?.data?.roce}%. ROE measures returns generated on shareholders' equity, while ROCE indicates how efficiently the company uses its capital.`
            : faqData?.data?.roce
            ? `${compName} has a ROCE of ${faqData?.data?.roce}%. ROCE indicates how efficiently the company uses its capital.`
            : `${compName} has a ROE of ${faqData?.data?.roe}%. ROE measures returns generated on shareholders' equity`
        }),
        {
            question: `Is ${compName} a good long-term investment?`,
            answer: `The long-term investment potential of ${compName} depends on factors such as earnings growth, debt management, and overall sector performance. Sustained profitability and controlled leverage could improve its long-term prospects.`
        },
        {
            question: `Is ${compName} undervalued or overvalued?`,
            answer: `Valuation ratios such as P/E, P/B, and EV/EBITDA can help determine whether ${compName} appears undervalued or overvalued at current levels. Detailed valuation metrics and peer comparisons are available on ${APP_NAME}`
        },
        {
            question: `How can I check ${compName}'s financials?`,
            answer: `You can access ${compName}'s financial statements, including the balance sheet, income statement, and quarterly results on ${APP_NAME}.`
        }
    ];
    

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "name": `${compName} Stock Market FAQs`,
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    let sector = "corporate";
    
    let industry = companyinfo?.company_basic_info ? JSON.parse(companyinfo?.company_basic_info)?.industry : 'corporate';

    //console.log('industry=====',industry)

    let i = industry?.toLowerCase();
    if(i?.includes("bank")){
        sector = "bank";
    }
    if(i?.includes("insurance")){
        sector = "insurance";
    }

    //console.log('sector=====',sector)

    const data = {
        company: `${compName}`,
        sector: industry,
        type: sector, // "bank" | "corporate"

        ratios: {
            pe: cData?.pe,
            pb: cData?.pb,
            currentRatio: extraRatio?.currentRatio,
            debtRatio: extraRatio?.debtRatio,
            debtToEquity: extraRatio?.debtToEquity,
        },

        profitability: {
            roa: extraRatio?.roa,
            roe: cData?.roe,
            operatingMargin: extraRatio?.operatingMargin
        },

        salesGrowth: extraRatio?.salesGrowth,

        shareholderReturns: {
            dividend: extraRatio?.dividend,
            dividendYield: extraRatio?.dividendYield,
            eps: cData?.eps
        },

        //bankingMetrics: null // not required for corporate
        bankingMetrics: {
            casa: null,
            advancesGrowth: extraRatio?.advancesGrowth,
            gnpa: extraRatio?.gnpa,
            nnpa: extraRatio?.nnpa,
            car: extraRatio?.capital_adq_ratio
        },
        corporateMetrics: {
            operatingMargin: extraRatio?.operatingMargin,
            debtRatio: extraRatio?.debtRatio,
            debtToEquity: extraRatio?.debtToEquity,
            roa: extraRatio?.roa,
            roe: cData?.roe
        },
        insuranceMetrics: {
            grossWrittenPremium: extraRatio?.grossWrittenPremium,
            netPremium: extraRatio?.netPremium,
            premiumGrowth3Y: extraRatio?.premiumGrowth3Y,
            solvencyRatio: extraRatio?.solvencyRatio,
            claimRatio: extraRatio?.claimRatio,
            combinedRatio: extraRatio?.combinedRatio,
            investmentIncome: extraRatio?.investmentIncome
        },
        growth: {
            revenue3Y: extraRatio?.revenue3ygrowth,
            yoyAdvance: extraRatio?.yoyAdvance,
            advance3Y: extraRatio?.advance3Y
        },
        otherIncome: extraRatio?.otherIncome // Cr
    };

    //console.log('company ratio=======', data)
    
    const formattedData = formatNumbersDeep(data, compactIN);
    

    return(
        <section className="bg-white shadow-md z-50 rounded-xl p-5">
            <div><StockAnalysis data={formattedData} /></div>
            <script type="application/ld+json">
                {JSON.stringify(faqSchema)}
            </script>
            <h2 className="text-2xl font-semibold py-4">{compName} Stock Market FAQs</h2>
            <div className="space-y-4">
                {faqs.map((faq, index) => (
                    <div key={index} className="border-b">
                        <div                             
                            className="w-full text-left py-4 font-semibold flex justify-between items-center"
                        >
                            Q{index+1}. {faq.question}
                            
                        </div>
                        <div className="pb-4 text-gray-600">
                            <span className="text-black">Ans:</span> {faq.answer}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}